import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Check, X } from "lucide-react";
import { toast } from "sonner";
import { NeuButton } from "../../components/neu/Neu";
import { RefBadge } from "../../components/neu/RefBadge";
import { decideRequest, approveResumeLeave } from "../../api/hr";
import { action as timeOffAction, approveCancel as approveTimeOffCancel } from "../../api/timeOff";
import { toneFor } from "./leaveTone";

const TONE = {
  pending: "text-amber-500", approved: "text-success", rejected: "text-destructive",
  completed: "text-success", cancelled: "text-muted-foreground", expired: "text-muted-foreground",
  cancel_requested: "text-amber-500",
};
const KIND_MAP = { leave: "leaves", advance: "advances", resignation: "resignations" };

// كارت طلب في سجل المدير بتصميم 3D Neu وشريط/شارة نوع ملوّنة + موافقة/رفض فوري للطلبات القابلة.
export const HistoryRow = ({ row, reload }) => {
  const { t } = useTranslation();
  const [busy, setBusy] = useState(false);
  const tone = toneFor(row.leaveType);
  const actionable =
    (row.status === "pending" && ["leave", "advance", "resignation", "timeOff"].includes(row.kind)) ||
    (row.status === "cancel_requested" && ["resume", "timeOff"].includes(row.kind));

  const decide = async (approve) => {
    setBusy(true);
    try {
      if (row.kind === "resume") await approveResumeLeave(row.id, approve, null);
      else if (row.kind === "timeOff" && row.status === "cancel_requested") await approveTimeOffCancel(row.id, approve, null);
      else if (row.kind === "timeOff") await timeOffAction(row.id, approve ? "approved" : "rejected");
      else await decideRequest(KIND_MAP[row.kind], row.id, approve ? "approve" : "reject", null);
      toast.success(t(`approvals.${approve ? "approve" : "reject"}d`));
      window.dispatchEvent(new Event("helcoo:approvals-refresh"));
      reload();
    } catch { toast.error(t("errors.generic")); }
    finally { setBusy(false); }
  };

  return (
    <div className="relative overflow-hidden rounded-2xl bg-background p-4 shadow-neu" data-testid={`history-row-${row.kind}-${row.id}`}>
      <span className={`absolute inset-y-0 start-0 w-1.5 ${tone.bar}`} aria-hidden="true" />
      <div className="flex items-center justify-between gap-3 ps-2">
        <RefBadge value={row.ref} />
        <span className={`shrink-0 text-base font-black ${TONE[row.status] || "text-muted-foreground"}`}>{row.statusLabel}</span>
      </div>
      <div className="mt-2 flex flex-wrap items-center gap-2 ps-2">
        <span className={`rounded-full px-2.5 py-0.5 text-xs font-black text-white ${tone.bar}`}>{row.kindLabel}</span>
        <p className="truncate text-lg font-bold">{row.who || "—"}</p>
      </div>
      <p className="truncate ps-2 text-base text-muted-foreground">{row.title}</p>
      {row.note ? <p className="truncate ps-2 text-sm text-muted-foreground">“{row.note}”</p> : null}
      {actionable ? (
        <div className="mt-2 flex gap-2 ps-2">
          <NeuButton variant="primary" className="flex items-center gap-1.5 px-3 py-1.5 text-sm" disabled={busy}
                     data-testid={`history-approve-${row.id}`} onClick={() => decide(true)}>
            <Check className="h-4 w-4" /> {t("approvals.approve")}
          </NeuButton>
          <NeuButton variant="danger" className="flex items-center gap-1.5 px-3 py-1.5 text-sm" disabled={busy}
                     data-testid={`history-reject-${row.id}`} onClick={() => decide(false)}>
            <X className="h-4 w-4" /> {t("approvals.reject")}
          </NeuButton>
        </div>
      ) : null}
    </div>
  );
};
